import { useCallback, useState } from "react";
import ToastService from "../../../services/toast.service";
import {
  serializeDraftForComparison,
  mergeGoal,
  createEmptyGoal,
  humanizeLabel,
} from "../helpers";

const INITIAL_GOAL_DIALOG = {
  open: false,
  form: null,
  groupLabel: "",
  itemLabel: "",
  readOnly: false,
};

export default function useGoalDialog({
  service,
  activeDraft,
  draft,
  isViewingHistory,
  findGoal,
  setDraft,
  setSavedDraftSnapshot,
  onGoalCountChanged,
}) {
  const [goalDialog, setGoalDialog] = useState(INITIAL_GOAL_DIALOG);
  const [isGoalSaving, setIsGoalSaving] = useState(false);
  const [isGoalDeleting, setIsGoalDeleting] = useState(false);

  const resetGoalDialog = useCallback(() => {
    setGoalDialog(INITIAL_GOAL_DIALOG);
  }, []);

  const closeGoalDialog = useCallback(() => {
    if (isGoalSaving) {
      return;
    }
    resetGoalDialog();
  }, [isGoalSaving, resetGoalDialog]);

  const openGoalDialog = useCallback((group, item) => {
    if (!activeDraft?.kpiDataId) {
      ToastService.warning("Load a scorecard before opening KPI focus notes.");
      return;
    }

    const existingGoal = findGoal(group.groupName, item.name);
    const form = existingGoal
      ? { ...existingGoal, id: existingGoal.id ?? 0, kpiDataId: activeDraft.kpiDataId }
      : createEmptyGoal(activeDraft.kpiDataId, group.groupName, item.name);

    setGoalDialog({
      open: true,
      form,
      groupLabel: humanizeLabel(group.groupName),
      itemLabel: humanizeLabel(item.name),
      itemValue: item.value,
      itemFormatting: item.formatting,
      itemType: item.type,
      readOnly: isViewingHistory,
    });
  }, [activeDraft, findGoal, isViewingHistory]);

  const updateGoalFormField = useCallback((field, value) => {
    setGoalDialog((currentDialog) => {
      if (!currentDialog.form || currentDialog.readOnly) {
        return currentDialog;
      }

      return {
        ...currentDialog,
        form: {
          ...currentDialog.form,
          [field]: value,
        },
      };
    });
  }, []);

  const saveGoal = useCallback(async () => {
    if (!goalDialog.form || !draft?.kpiDataId) {
      return;
    }

    setIsGoalSaving(true);

    try {
      const isNewGoal = !goalDialog.form.id;
      const payload = {
        ...goalDialog.form,
        id: goalDialog.form.id ?? 0,
        kpiDataId: draft.kpiDataId,
      };
      const savedGoal = await service.saveGoal(payload);

      let goalWasAdded = false;
      setDraft((currentDraft) => {
        if (!currentDraft) {
          return currentDraft;
        }

        const currentGoals = currentDraft.goals ?? [];
        const nextGoals = mergeGoal(currentGoals, savedGoal);
        goalWasAdded = isNewGoal && nextGoals.length > currentGoals.length;
        const nextDraft = { ...currentDraft, goals: nextGoals };
        setSavedDraftSnapshot(serializeDraftForComparison(nextDraft));
        return nextDraft;
      });

      if (goalWasAdded) {
        onGoalCountChanged(draft.kpiDataId, 1);
      }

      ToastService.success("KPI focus saved.");
      resetGoalDialog();
    } catch {
      // Error already logged in service
    } finally {
      setIsGoalSaving(false);
    }
  }, [draft, goalDialog.form, resetGoalDialog, service, setDraft, setSavedDraftSnapshot, onGoalCountChanged]);

  const deleteGoal = useCallback(async () => {
    if (!goalDialog.form?.id || !draft?.kpiDataId) {
      return;
    }

    setIsGoalDeleting(true);

    try {
      await service.deleteGoal(goalDialog.form.id, draft.kpiDataId);

      setDraft((currentDraft) => {
        if (!currentDraft) {
          return currentDraft;
        }

        const nextDraft = {
          ...currentDraft,
          goals: (currentDraft.goals ?? []).filter((g) => g.id !== goalDialog.form.id),
        };
        setSavedDraftSnapshot(serializeDraftForComparison(nextDraft));
        return nextDraft;
      });

      onGoalCountChanged(draft.kpiDataId, -1);

      ToastService.success("KPI focus deleted.");
      resetGoalDialog();
    } catch {
      // Error already logged in service
    } finally {
      setIsGoalDeleting(false);
    }
  }, [draft, goalDialog.form, resetGoalDialog, service, setDraft, setSavedDraftSnapshot, onGoalCountChanged]);

  return {
    goalDialog,
    isGoalSaving,
    isGoalDeleting,
    resetGoalDialog,
    closeGoalDialog,
    openGoalDialog,
    updateGoalFormField,
    saveGoal,
    deleteGoal,
  };
}
